import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useCart } from '../../context/CartContext';
import logo from '../../assets/logo-getit.png';
import logoPosgo from '../../assets/logo-posgo.png';
import ConfirmCancelModal from '../shared/ConfirmCancelModal';

const getMimeType = (b64) => b64?.startsWith('iVBOR') ? 'image/png' : 'image/jpeg';

const CategorySelectionScreen = () => {
    const { categories, sessionData, categoryImages } = useAppContext();
    const { items, clearCart } = useCart();
    const navigate = useNavigate();
    const totalItems = items.reduce((sum, item) => sum + (item.quantity || 1), 0);
    const [showConfirm, setShowConfirm] = useState(false);

    const categoriesWithProducts = useMemo(() => {
        if (!categories || !sessionData?.products) return [];

        const productCategoryIds = new Set(
            sessionData.products
                .filter(product => product.is_totem)
                .map(product => product.category_id)
        );

        return categories.filter(category => productCategoryIds.has(category.id));
    }, [categories, sessionData]);

    const handleCategoryClick = (categoryId) => {
        const productsInCategory = sessionData?.products?.filter(
            product => product.is_totem && product.category_id === categoryId
        ) || [];

        const hasSubcategories = productsInCategory.some(
            product => product.subcategory_id && product.subcategory_name
        );

        if (hasSubcategories) {
            navigate(`/subcategories/${categoryId}`);
        } else {
            navigate(`/products/${categoryId}`);
        }
    };

    const handleShowAll = () => {
        navigate('/products/all');
    };

    return (
        <>
        <div className="category-screen">
            <div className="category-header">
                <img src={logo} alt="GetIT" className="logo" />
                <div className="posgo-badge">
                    <img src={logoPosgo} alt="PosGo" className="posgo-logo" />
                    <span className="posgo-text">PosGo.cl</span>
                </div>
            </div>

            <div className="category-body" style={totalItems > 0 ? { paddingBottom: '5rem' } : {}}>
                <h1 className="menu-title">Menú</h1>

                <div className="category-grid">
                    {categoriesWithProducts.map((category) => (
                        <button
                            key={category.id}
                            className="category-card"
                            onClick={() => handleCategoryClick(category.id)}
                        >
                            <div className="category-image">
                                {categoryImages[category.id] && (
                                    <img src={`data:${getMimeType(categoryImages[category.id])};base64,${categoryImages[category.id]}`} alt="" />
                                )}
                            </div>
                            <div className="category-name">{category.name}</div>
                        </button>
                    ))}
                </div>

                <button className="btn-all" onClick={handleShowAll}>
                    Ver Todos los Productos
                </button>
            </div>

            {totalItems > 0 && (
                <div className="bottom-bar">
                    <button className="btn-cancel" onClick={() => setShowConfirm(true)}>
                        Cancelar todo
                    </button>
                    <button className="btn-pay" onClick={() => navigate('/payment')}>
                        Ir a pagar ({totalItems})
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                    </button>
                </div>
            )}

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@900&display=swap');

                * {
                    box-sizing: border-box;
                }

                .category-screen {
                    min-height: 100vh;
                    display: flex;
                    flex-direction: column;
                    background: #f5f5f5;
                    overflow-x: hidden;
                }

                .category-header {
                    background: white;
                    padding: 1.5rem;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
                    position: sticky;
                    top: 0;
                    z-index: 100;
                }

                .logo {
                    height: 100px;
                    width: auto;
                }

                .posgo-badge {
                    position: absolute;
                    right: 1.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                }

                .posgo-logo {
                    height: 80px;
                    width: auto;
                }

                .posgo-text {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #db2113;
                    font-family: Quicksand, sans-serif;
                }

                .category-body {
                    flex: 1;
                    padding: 0.3rem 1.5rem;
                    max-width: 1800px;
                    width: 100%;
                    margin: 0 auto;
                    box-sizing: border-box;
                }

                .menu-title {
                    text-align: center;
                    font-size: 4rem;
                    font-weight: 900;
                    font-family: 'Montserrat', sans-serif;
                    color: #0E8164;
                    margin: 0 0 0.3rem 0;
                    letter-spacing: -0.02em;
                }

                .category-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 2.5rem;
                    margin-bottom: 2rem;
                }

                .category-card {
                    background: white;
                    border: 3px solid transparent;
                    border-radius: 20px;
                    padding: 1.5rem 1rem;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 1rem;
                    min-height: 200px;
                    box-sizing: border-box;
                    min-width: 0;
                }

                .category-card:hover {
                    border-color: #0E8164;
                    transform: translateY(-6px);
                    box-shadow: 0 12px 24px rgba(14, 129, 100, 0.2);
                }

                .category-image {
                    width: min(200px, 80%);
                    aspect-ratio: 1;
                    border-radius: 16px;
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .category-image img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .category-name {
                    font-size: 1.875rem;
                    font-weight: 700;
                    color: #1a1a1a;
                    text-align: center;
                    line-height: 1.3;
                }

                .btn-all {
                    display: block;
                    margin: 0 auto 2.5rem;
                    background: #DC2626;
                    color: white;
                    border: none;
                    border-radius: 12px;
                    padding: 0.875rem 3rem;
                    font-size: 1.25rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 4px 16px rgba(220, 38, 38, 0.3);
                }

                .btn-all:hover {
                    background: #B91C1C;
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(220, 38, 38, 0.4);
                }

                @media (max-width: 1400px) {
                    .category-grid {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 2rem;
                    }
                }

                @media (max-width: 1024px) {
                    .category-grid {
                        grid-template-columns: repeat(3, 1fr);
                    }
                }

                @media (max-width: 768px) {
                    .category-body {
                        padding: 2rem 1.5rem;
                    }

                    .menu-title {
                        font-size: 3rem;
                    }

                    .category-grid {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 1.5rem;
                    }

                    .category-card {
                        min-height: 280px;
                    }

                    .category-image {
                        width: 160px;
                        height: 160px;
                    }

                    .category-name {
                        font-size: 1.5rem;
                    }

                    .btn-all {
                        width: 100%;
                        padding: 1.5rem 3rem;
                        font-size: 1.5rem;
                    }
                }

                .bottom-bar {
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    background: white;
                    padding: 0.75rem 2rem;
                    display: flex;
                    justify-content: flex-end;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 -4px 16px rgba(0,0,0,0.08);
                    z-index: 40;
                }

                .btn-cancel {
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                    background: white;
                    color: #DC2626;
                    border: 2px solid #DC2626;
                    border-radius: 50px;
                    padding: 0.65rem 1.4rem;
                    font-size: 0.9rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .btn-cancel:hover {
                    background: #fee2e2;
                }

                .btn-pay {
                    display: flex;
                    align-items: center;
                    gap: 0.6rem;
                    background: #DC2626;
                    color: white;
                    border: none;
                    border-radius: 50px;
                    padding: 0.9rem 1.8rem;
                    font-size: 1rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .btn-pay:hover {
                    background: #B91C1C;
                }
            `}</style>
        </div>

        <ConfirmCancelModal
            isOpen={showConfirm}
            onConfirm={() => { setShowConfirm(false); clearCart(); navigate('/'); }}
            onCancel={() => setShowConfirm(false)}
        />
        </>
    );
};

export default CategorySelectionScreen;